import { useCallback } from 'react';

import { useAdminCompanyController_removeUser } from '@api-client/generated/AdminCompanyController/removeUser';
import { useQueryClient } from '@tanstack/react-query';

/**
 * Raised when the backend rejects a detach because it would remove the
 * company's last member. The caller's roster snapshot may be stale (another
 * admin changed membership), so it should refetch and surface a conflict
 * rather than reporting a successful removal.
 */
export const LAST_COMPANY_USER_ERROR = 'last_company_user';

/** Query-key roots that display membership data and must refresh after a
 * detach: the per-company Team roster and the cross-company Access Map. */
const MEMBERSHIP_QUERY_ROOTS = [
  'AdminCompanyController_listMemberships',
  'accessMap',
];

/**
 * Reusable detach unit shared by the Team tab and the Access Map. Removes a
 * member from a company and invalidates every membership-backed query so
 * rosters and the access map stay in sync. Kept independent of any single
 * company's list query so the Access Map can reuse it without refetching a
 * company it isn't showing.
 */
export const useDetachMember = () => {
  const queryClient = useQueryClient();
  const removeMutation = useAdminCompanyController_removeUser();

  const invalidateMemberships = useCallback(
    () =>
      queryClient.invalidateQueries({
        predicate: (query) =>
          MEMBERSHIP_QUERY_ROOTS.includes(query.queryKey[0] as string),
      }),
    [queryClient]
  );

  const detach = useCallback(
    (companyId: string, userId: string) =>
      removeMutation
        .mutateAsync({ parameter: { id: companyId, userId } })
        .finally(() => {
          void invalidateMemberships();
        }),
    [removeMutation, invalidateMemberships]
  );

  return {
    detach,
    isDetaching: removeMutation.isPending,
    invalidateMemberships,
  };
};
